/**
 * Pixelgenaue Textmessung über ein Singleton-Canvas mit der tatsächlich gerenderten Schrift.
 * Ersetzt Zeichenzahl × Pauschalbreite, die systematisch überschätzt und damit Spalten zu breit
 * gemacht hat.
 *
 * Die Schrift wird aus einem echten DOM-Element gelesen. Steht das beim ersten Aufruf noch nicht,
 * wird mit der body-Schrift gemessen und beim nächsten Aufruf erneut versucht — erst mit einem
 * echten Element gilt der Kontext als endgültig.
 */
export function createFontMeasureContext(
  sampleSelector: string,
  fallbackFontWeight: string
): () => CanvasRenderingContext2D | null {
  let context: CanvasRenderingContext2D | null = null;
  let settled = false;

  return () => {
    if (settled) return context;

    if (typeof document === "undefined" || typeof window === "undefined") {
      settled = true;
      return null;
    }

    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) {
      settled = true;
      return null;
    }

    const sample = document.querySelector(sampleSelector) as HTMLElement | null;
    const styleSource = sample ?? document.body;
    const cs = window.getComputedStyle(styleSource);
    ctx.font = `${cs.fontWeight || fallbackFontWeight} ${cs.fontSize || "14px"} ${cs.fontFamily || "sans-serif"}`;

    context = ctx;
    if (sample) settled = true;
    return ctx;
  };
}

/** Textbreite in Pixeln; ohne DOM (SSR) Rückfall auf die Zeichenzahl-Schätzung. */
export function measureTextWidth(
  measureContext: () => CanvasRenderingContext2D | null,
  text: string,
  fallbackCharWidthPx: number
): number {
  const ctx = measureContext();
  if (ctx) return Math.ceil(ctx.measureText(text).width);
  return Math.ceil(text.length * fallbackCharWidthPx);
}

let fontMeasureCanvas: CanvasRenderingContext2D | null | undefined;

/** Messung mit der Schrift eines konkreten Elements — nicht mit dem ersten Treffer einer Seite. */
export function measureTextWidthWithFont(
  font: string,
  text: string,
  fallbackCharWidthPx: number
): number {
  if (typeof document === "undefined") return Math.ceil(text.length * fallbackCharWidthPx);
  if (fontMeasureCanvas === undefined) {
    fontMeasureCanvas = document.createElement("canvas").getContext("2d");
  }
  if (!fontMeasureCanvas) return Math.ceil(text.length * fallbackCharWidthPx);
  fontMeasureCanvas.font = font;
  return Math.ceil(fontMeasureCanvas.measureText(text).width);
}
